import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Edit2, CheckCircle } from 'lucide-react';

const OpinionQuotas = ({ scriptId = '6a90a34143673ea8265d17ce' }) => {
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    field: '',
    value: '',
    target: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, quotaId: null });

  const apiBase = process.env.REACT_APP_AGENT_API_URL || '';

  const fetchQuotas = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${apiBase}/api/quotas?scriptId=${scriptId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load quotas: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setQuotas(data.quotas || []);
      }
    } catch (err) {
      console.error('Error fetching quotas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [scriptId, apiBase]);

  useEffect(() => {
    fetchQuotas();
  }, [fetchQuotas]);

  const handleAddQuota = () => {
    setEditingId(null);
    setFormData({ name: '', field: '', value: '', target: '' });
    setShowForm(true);
  };

  const handleEditQuota = (quota) => {
    setEditingId(quota.id);
    setFormData({
      name: quota.name,
      field: quota.field,
      value: quota.value || '',
      target: quota.target,
    });
    setShowForm(true);
  };

  const handleSaveQuota = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.field || !formData.target) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        scriptId,
        name: formData.name,
        field: formData.field,
        value: formData.value || null,
        target: Number(formData.target),
      };

      const url = editingId ? `${apiBase}/api/quotas/${editingId}` : `${apiBase}/api/quotas`;
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to save quota: ${response.status}`);
      }

      setShowForm(false);
      setFormData({ name: '', field: '', value: '', target: '' });
      await fetchQuotas();
    } catch (err) {
      console.error('Error saving quota:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuota = (quotaId) => {
    setDeleteConfirm({ open: true, quotaId });
  };

  const confirmDelete = async () => {
    const quotaId = deleteConfirm.quotaId;
    setDeleteConfirm({ open: false, quotaId: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBase}/api/quotas/${quotaId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete quota: ${response.status}`);
      }

      await fetchQuotas();
    } catch (err) {
      console.error('Error deleting quota:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const getProgressPercentage = (quota) => {
    if (quota.target === 0) return 0;
    return Math.min(100, Math.round((quota.progress / quota.target) * 100));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">📊 Survey Quotas</h2>
          <p className="text-gray-600 text-sm mt-1">Manage and track survey response quotas</p>
        </div>
        <button
          onClick={handleAddQuota}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={18} />
          Add Quota
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading quotas...</p>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {editingId ? 'Edit Quota' : 'Create New Quota'}
          </h3>
          <form onSubmit={handleSaveQuota} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Quota Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Age 18-25"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Demographic Field *
                </label>
                <input
                  type="text"
                  value={formData.field}
                  onChange={(e) => setFormData({ ...formData, field: e.target.value })}
                  placeholder="e.g., age, race, gender"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Field Value (optional)
                </label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="e.g., 18-25, African American"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Target Count *
                </label>
                <input
                  type="number"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  placeholder="e.g., 20"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 font-medium"
              >
                {saving ? 'Saving...' : 'Save Quota'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400 transition font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Quotas list */}
      {!loading && !showForm && quotas.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">No Quotas Yet</h3>
          <p className="text-gray-600 mb-6">Create quotas to track survey response goals.</p>
          <button
            onClick={handleAddQuota}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={18} />
            Create First Quota
          </button>
        </div>
      )}

      {!loading && quotas.length > 0 && (
        <div className="space-y-4">
          {quotas.map((quota) => {
            const percentage = getProgressPercentage(quota);
            const isComplete = quota.isComplete;

            return (
              <div key={quota.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">{quota.name}</h3>
                      {isComplete && (
                        <CheckCircle size={20} className="text-green-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {quota.field}
                      {quota.value ? ` - ${quota.value}` : ''}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditQuota(quota)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Edit quota"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteQuota(quota.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete quota"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Progress</span>
                    <span className={`font-semibold ${isComplete ? 'text-green-600' : 'text-blue-600'}`}>
                      {quota.progress} / {quota.target}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isComplete ? 'bg-green-600' : 'bg-blue-600'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="text-sm text-gray-600 text-right">
                    {percentage}% complete
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Quota</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this quota? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm({ open: false, quotaId: null })}
                className="px-4 py-2 bg-gray-300 text-gray-900 rounded-lg hover:bg-gray-400 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpinionQuotas;
